import { useQueryParams } from 'use-query-params'
import React, { Fragment, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { LntButton, LntDropdown } from '@app/components/base'
import { Icon, Show } from '@app/components/util'
import { ViewingLogsDrawer } from '@app/drawers'
import { CommonTasksItemsModal, RestartMonitoringTaskModal } from '@app/modals'
import { ContentLayout } from '@app/layouts'
import {
  ExtendedTable,
  Header,
  PermissionWrapper,
  SubheaderToolbarDivider,
  TableSettingsToolbar
} from '@app/components'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { FILE_TYPES, MONITORING_TASK_STATUS } from '@app/api/constants'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'
import { MONITORINGS_TASKS_KEY } from '@app/constants/queryKeys'
import { PERMISSION } from '@app/constants/permission'
import { ICON_NAME } from '@app/constants/icon'

import { useNotification, usePaginationQueryParams, useTableSearch, useTableSetting, useToggle } from '@app/hooks'

import { getExportFilesDropdownMenu } from '@app/utils'
import { exportAllMonitoringsRegistryFile } from '@app/utils/export'
import { objectHasEveryNullableProperties } from '@app/utils/object'

import {
  getTasks,
  recoveryTasksItems,
  restartTasksItems,
  stopTasksItems,
  sendNotificationSelected,
  sendNotificationsEveryone,
  restartTotalCheckItem
} from '@app/api/routes/monitoringTasks'

import { getColumns, adaptMonitoringRegistryTableData, adaptMonitoringTasksFilerData } from './lib'
import { TopFilters } from './ui'
import { useToggleToolbarButtons } from './hooks'
import { topFiltersQuerySchema, tableSearchQuerySchema, columnsSearchSchema } from './configs'

import type { Key, ReactNode } from 'react'
import type { FiltersParams } from '@app/types/filter'
import type { GetTasksParams } from '@app/api/routes/monitoringTasks/types'
import type { RestartMonitoringTaskFormData } from '@app/modals/RestartMonitoringTaskModal/types'
import type { FileType } from '@app/types'
import type { MonitoringRegistryDataSource } from './types'

const columns = getColumns()

const title = 'Реестр мониторингов'

export const MonitoringRegistryPage: React.FC = () => {
  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationQueryParams()
  const [queryFilters, setQueryFilters] = useQueryParams(topFiltersQuerySchema)
  const notification = useNotification()
  const queryClient = useQueryClient()

  const viewLogsDrawerToggle = useToggle()
  const recoveryDrawerToggle = useToggle()
  const stopDrawerToggle = useToggle()
  const restartDrawerToggle = useToggle()
  const restartTotalCheckModalToggle = useToggle()

  const buttonLoadingExportAllToggle = useToggle()

  const [rowSelectionKeys, setRowSelectionKeys] = useState<Key[]>([])
  const [selectedDataRecords, setSelectedDataRecords] = useState<MonitoringRegistryDataSource[]>([])

  const monitoringsTasksParams = useMemo(
    () => ({ ...paginationParams, ...queryFilters }),
    [paginationParams, queryFilters]
  ) as GetTasksParams

  const { data: monitoringRegistryTableData, isLoading: isMonitoringsTasksDataLoading } = useQuery({
    enabled: !objectHasEveryNullableProperties(queryFilters),
    queryKey: [MONITORINGS_TASKS_KEY.tasksItems, monitoringsTasksParams],
    queryFn: async () => await getTasks(monitoringsTasksParams),
    select: tasksData => adaptMonitoringRegistryTableData(tasksData)
  })

  const handleSuccessMutation = (description: string): void => {
    setRowSelectionKeys([])
    handleUnselectToolbarSelectedButtons()

    notification.success({
      message: 'Успешно:',
      description
    })
  }

  const recoveryTasksItemsMutation = useMutation({
    mutationFn: recoveryTasksItems,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItems] })

      recoveryDrawerToggle.setUntoggled()

      handleSuccessMutation('Выбранные мониторинги успешно восстановлены')
    }
  })

  const stopTasksItemsMutation = useMutation({
    mutationFn: stopTasksItems,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItems] })

      stopDrawerToggle.setUntoggled()

      handleSuccessMutation('Выбранные мониторинги успешно остановлены')
    }
  })

  const restartTasksItemsMutation = useMutation({
    mutationFn: restartTasksItems,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItems] })

      restartDrawerToggle.setUntoggled()

      handleSuccessMutation('Выбранные мониторинги успешно перезапущены')
    }
  })

  const restartTotalCheckMutation = useMutation({
    mutationFn: restartTotalCheckItem,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItems] })

      restartTotalCheckModalToggle.setUntoggled()

      handleSuccessMutation('Выбранный мониторинг успешно перезапущен')
    }
  })

  const sendNotificationSelectedMutation = useMutation({
    mutationFn: sendNotificationSelected,
    onSuccess: async () => {
      notification.success({
        message: 'Уведомления отправлены',
        description: 'Все уведомления успешно отправлены'
      })
    }
  })

  const sendNotificationsEveryoneMutation = useMutation({
    mutationFn: sendNotificationsEveryone,
    onSuccess: async () => {
      notification.success({
        message: 'Уведомления отправлены',
        description: 'Все уведомления успешно отправлены'
      })
    }
  })

  const { stopButtonToggle, restartButtonToggle, recoveryButtonToggle, handleUnselectToolbarSelectedButtons } =
    useToggleToolbarButtons(rowSelectionKeys, selectedDataRecords)

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleFiltersChange = (filters: FiltersParams): void => {
    setQueryFilters(filters)
    resetCurrentPage()
  }

  const handleRecoveryTasksItems = (): void => {
    const ids = selectedDataRecords
      .filter(record => record.status === MONITORING_TASK_STATUS.STOPPED)
      .map(record => record.monitoringId)

    recoveryTasksItemsMutation.mutate({ ids })
  }

  const handleStopTasksItems = (): void => {
    const ids = selectedDataRecords
      .filter(record => record.status !== MONITORING_TASK_STATUS.STOPPED)
      .map(record => record.monitoringId)

    stopTasksItemsMutation.mutate({ ids })
  }

  const handleRestartTasksItems = (): void => {
    const ids = selectedDataRecords
      .filter(record => record.status !== MONITORING_TASK_STATUS.START)
      .map(record => record.monitoringId)

    restartTasksItemsMutation.mutate({ ids })
  }

  const handleSelectedRowChange = (keys: Key[], selectedRecords: MonitoringRegistryDataSource[]): void => {
    setRowSelectionKeys(keys)
    setSelectedDataRecords(selectedRecords)
  }

  const handleSendNotificationsEveryone = (): void => {
    const monitoringTasksFilerData = adaptMonitoringTasksFilerData(queryFilters)

    sendNotificationsEveryoneMutation.mutate(monitoringTasksFilerData)
  }

  const handleSendNotificationSelected = (): void => {
    const selectedIds = rowSelectionKeys.map(Number)
    sendNotificationSelectedMutation.mutate({ ids: selectedIds })
  }

  const handleExportAllMonitoringsRegistry = async (fileType: FileType): Promise<void> => {
    buttonLoadingExportAllToggle.setToggled()

    const monitoringTasksFilerData = adaptMonitoringTasksFilerData(queryFilters)

    const data = {
      ...monitoringTasksFilerData,
      fileFormat: fileType
    }

    const fileName = 'Задачи на мониторинг'

    try {
      await exportAllMonitoringsRegistryFile(fileName, data)
    } finally {
      buttonLoadingExportAllToggle.setUntoggled()
    }
  }

  const handleRestartMonitoringTask = (): void => {
    if (selectedDataRecords[0].monitoringType === 'TOTAL_CHECK') {
      restartTotalCheckModalToggle.setToggled()
    } else {
      restartDrawerToggle.setToggled()
    }
  }

  const handleRestartTotalCheck = ({ competitorIds }: RestartMonitoringTaskFormData): void => {
    restartTotalCheckMutation.mutate({
      ids: rowSelectionKeys.map(String),
      competitorIds
    })
  }

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.monitoringRegistryPage, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredMonitoringsRegistryDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: monitoringRegistryTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const exportDropdownItems = getExportFilesDropdownMenu(
    'Задачи на мониторинг',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportAllMonitoringsRegistry
  )

  const toolbar: ReactNode[] = [
    <Show
      key='show-log-button'
      condition={ rowSelectionKeys.length === 1 }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.viewMonitoringTasksList] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ viewLogsDrawerToggle.setToggled }
        >
          Посмотреть лог
        </LntButton>

        <SubheaderToolbarDivider />
      </PermissionWrapper>
    </Show>,

    <Fragment key='monitoring-registry-control-buttons'>
      <Show condition={ recoveryButtonToggle.value }>
        <PermissionWrapper requiredPermissions={ [PERMISSION.startRestartMonitoringTask] }>
          <LntButton
            type={ BUTTON_TYPE.text }
            size={ SIZE.small }
            onClick={ recoveryDrawerToggle.setToggled }
          >
            Восстановить
          </LntButton>
        </PermissionWrapper>
      </Show>

      <Show condition={ restartButtonToggle.value }>
        <PermissionWrapper requiredPermissions={ [PERMISSION.startRestartMonitoringTask] }>
          <LntButton
            type={ BUTTON_TYPE.text }
            size={ SIZE.small }
            onClick={ handleRestartMonitoringTask }
          >
            Перезапустить
          </LntButton>
        </PermissionWrapper>
      </Show>

      <Show condition={ stopButtonToggle.value }>
        <PermissionWrapper requiredPermissions={ [PERMISSION.stopMonitoringTask] }>
          <LntButton
            type={ BUTTON_TYPE.text }
            size={ SIZE.small }
            onClick={ stopDrawerToggle.setToggled }
          >
            Остановить
          </LntButton>
        </PermissionWrapper>
      </Show>

      <Show condition={ !!monitoringRegistryTableData?.count }>
        <PermissionWrapper requiredPermissions={ [PERMISSION.viewMonitoringTasksList] }>
          <LntDropdown
            trigger={ ['click'] }
            items={ exportDropdownItems }
          >
            <LntButton
              type={ BUTTON_TYPE.text }
              size={ SIZE.small }
              loading={ buttonLoadingExportAllToggle.value }
            >
              Экспорт всех
              <Icon name={ ICON_NAME.arrowDown } />
            </LntButton>
          </LntDropdown>
        </PermissionWrapper>
      </Show>

      <Show
        condition={
          recoveryButtonToggle.value ||
          restartButtonToggle.value ||
          stopButtonToggle.value ||
          !!monitoringRegistryTableData?.count
        }
      >
        <SubheaderToolbarDivider />
      </Show>
    </Fragment>,

    <Fragment key='send-notification-button'>
      <Show condition={ !!rowSelectionKeys.length }>
        <PermissionWrapper requiredPermissions={ [PERMISSION.sendNotificationContractors] }>
          <LntButton
            type={ BUTTON_TYPE.secondary }
            size={ SIZE.small }
            onClick={ handleSendNotificationSelected }
          >
            Уведомить выбранных
          </LntButton>
        </PermissionWrapper>
      </Show>

      <Show condition={ !!monitoringRegistryTableData?.count }>
        <PermissionWrapper requiredPermissions={ [PERMISSION.sendNotificationContractors] }>
          <LntButton
            type={ BUTTON_TYPE.primary }
            size={ SIZE.small }
            onClick={ handleSendNotificationsEveryone }
          >
            Уведомить всех
          </LntButton>
        </PermissionWrapper>
      </Show>

      <Show condition={ !!rowSelectionKeys.length || !!monitoringRegistryTableData?.count }>
        <SubheaderToolbarDivider />
      </Show>
    </Fragment>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  return (
    <>
      <Header controlProps={ { title } } />

      <ContentLayout>
        <TopFilters
          onSearch={ handleFiltersChange }
          initialFilters={ queryFilters as FiltersParams }
        />

        <ExtendedTable
          loading={ isMonitoringsTasksDataLoading }
          columns={ columnsWithSearchTool }
          dataSource={ filteredMonitoringsRegistryDataSource }
          toolbar={ toolbar }
          rowSelection={ {
            selectedRowKeys: rowSelectionKeys,
            onChange: handleSelectedRowChange
          } }
          pagination={ {
            ...pagination,
            total: monitoringRegistryTableData?.count,
            onChange: handlePaginationChange
          } }
        />
      </ContentLayout>

      <Show condition={ rowSelectionKeys.length === 1 }>
        <ViewingLogsDrawer
          open={ viewLogsDrawerToggle.value }
          onClose={ viewLogsDrawerToggle.setUntoggled }
          taskId={ selectedDataRecords?.length && selectedDataRecords[0].monitoringId }
          width={ 424 }
        />
      </Show>

      <CommonTasksItemsModal
        title='Вы уверены, что хотите восстановить выбранные задачи?'
        open={ recoveryDrawerToggle.value }
        onCancel={ recoveryDrawerToggle.setUntoggled }
        onSubmit={ handleRecoveryTasksItems }
        cancelText='Отмена'
        okText='Восстановить'
      />

      <CommonTasksItemsModal
        title='Вы уверены, что хотите остановить выбранные задачи?'
        open={ stopDrawerToggle.value }
        onCancel={ stopDrawerToggle.setUntoggled }
        onSubmit={ handleStopTasksItems }
        cancelText='Отмена'
        okText='Остановить'
      />

      <CommonTasksItemsModal
        title='Вы уверены, что хотите перезапустить выбранные задачи?'
        open={ restartDrawerToggle.value }
        onCancel={ restartDrawerToggle.setUntoggled }
        onSubmit={ handleRestartTasksItems }
        cancelText='Отмена'
        okText='Перезапустить'
      />

      <RestartMonitoringTaskModal
        open={ restartTotalCheckModalToggle.value }
        onCancel={ restartTotalCheckModalToggle.setUntoggled }
        onSubmit={ handleRestartTotalCheck }
      />
    </>
  )
}
